import { providePrimeNG } from "primeng/config";
import { ToastModule } from "primeng/toast";
import { MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { GalleryModule } from "ng-gallery";
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection
} from "@angular/core";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideRouter, withDisabledInitialNavigation, withRouterConfig, Router } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { LIGHTBOX_CONFIG, LightboxConfig } from "ng-gallery/lightbox";
import { routes } from "./app.routes";
import { AuthService } from "./core/authentication/auth.service";
import { AuthHeartbeatService } from "./core/authentication/auth-heartbeat.service";
import { GlobalErrorHandler } from "./shared/services/telemetry-errorhandler.service";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { authTokenInterceptor } from "./core/authentication/auth-token.interceptor";
import SyncManager from "./core/sync/sync-manager";
import AppFocusEventHandler from "./shared/services/app-focus-event-handler";
import { loggingInterceptor } from "./core/interceptors/http-logging.interceptor";
import BqaTheme from "../theme/bqa.theme";
import { environment } from "./core/config/environment";
import { getEnvironmentName } from "./shared/utils/environment-helper";

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withInterceptors([authTokenInterceptor, loggingInterceptor])
    ),
    provideZoneChangeDetection({
      eventCoalescing: true
    }),
    provideRouter(routes, withDisabledInitialNavigation(), withRouterConfig({ onSameUrlNavigation: "reload" })),
    provideServiceWorker("ngsw-worker.js", {
      enabled: environment.useServiceWorker,
      registrationStrategy: "registerWhenStable:30000",
    }),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: BqaTheme,
        options: {
          darkModeSelector: false || "none",
          cssLayer: {
            name: "primeng",
            order: "primeng, bqa"
          }
        }
      },
      ripple: true
    }),
    importProvidersFrom(GalleryModule),
    {
      provide: LIGHTBOX_CONFIG,
      useValue: {
        keyboardShortcuts: false,
        exitAnimationTime: 1000
      } as LightboxConfig
    },
    provideAppInitializer(() => {
      const syncManager = inject(SyncManager);
      syncManager.start();
    }),
    AuthService,
    provideAppInitializer(async () => {
      const auth = inject(AuthService);
      const router = inject(Router);

      if (!auth.isExternallyAuthenticated()) {
        await auth.init();
        await auth.handleRedirect();
      }

      if (getEnvironmentName() !== "production") {
        console.log("[AppConfig] Initialized -- useServiceWorker:", environment.useServiceWorker);
      }

      router.initialNavigation();
    }),
    provideAppInitializer(() =>
      inject(AuthHeartbeatService).start()),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    importProvidersFrom(ToastModule, ButtonModule),
    MessageService,
    provideAppInitializer(() => {
      inject(AppFocusEventHandler);
    }),
  ]
};
